import { useMemo, useState } from 'react';

import { RICHTEXT_SETTINGS, ViewProps } from './table.constants';
import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import SubActivityPrint from '../sub-activity/sub-activity-print';
import { ColGroupsView } from './table-common';
import { getRichtextValue } from '../shared/logic/get-richtext-value';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import {
  getEditableCellsRowHeight,
  getTableHeaderAndBodyRows,
  isCellEmpty,
} from './table-view.utils';
import * as Styled from './table-print.style';
import { useActivityAnswersPrintContext } from 'approot/activity-builder/print/answers/activity-answers-print.context';

const TablePrintAnswers = ({
  properties,
  subActivityProperties,
  id,
  studentId,
}: ViewProps & {
  showAnswers?: boolean;
  studentId?: number;
}) => {
  const table = properties.table;
  const extensions = getExtensions(RICHTEXT_SETTINGS);
  const [tableWrapperRef, setTableWrapperRef] = useState<HTMLDivElement | null>(
    null
  );
  const { theme } = useActivityBuilderTheme();
  const { answers: sourceAnswers } = useActivityAnswersPrintContext(id);

  const answers = useMemo(
    () =>
      studentId
        ? sourceAnswers?.filter(a => a.studentId === studentId)
        : sourceAnswers,
    [sourceAnswers, studentId]
  );

  const { tableHeaderRow, tableBodyRows } = useMemo(
    () => getTableHeaderAndBodyRows(table),
    [table]
  );

  const headerTextAreaRowHeight = useMemo(() => {
    return getEditableCellsRowHeight(tableHeaderRow?.cells || []);
  }, [tableHeaderRow]);

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <div ref={setTableWrapperRef}>
        <Styled.Table key={`rows-${table.rows.length}`}>
          <ColGroupsView
            columnWidths={table.columnWidths}
            containerElement={tableWrapperRef}
          />
          {tableHeaderRow && (
            <thead>
              <Styled.TableRow>
                {tableHeaderRow?.cells.map((header, index) => {
                  const cellAnswer =
                    answers?.find(
                      ans =>
                        ans.answerReferences?.row === 0 &&
                        ans.answerReferences.col === index
                    ) || null;

                  return (
                    <Styled.TableCell $highlight key={`row-1-column-${index}`}>
                      {cellAnswer ? (
                        <Styled.TableCellView
                          $highlight
                          $isAnswer={!!cellAnswer}
                          $baseFontSize={theme.font.medium.size}
                          $baseLineHeight={theme.font.small.lineHeight}
                          $rows={headerTextAreaRowHeight}
                          dangerouslySetInnerHTML={{
                            __html: !isCellEmpty(header.value)
                              ? getRichtextValue(header.value, extensions)
                              : getRichtextValue(
                                  getRichtextAnswer(cellAnswer),
                                  extensions
                                ),
                          }}
                        />
                      ) : (
                        <Styled.TableCellView
                          $highlight
                          $baseFontSize={theme.font.medium.size}
                          $baseLineHeight={theme.font.small.lineHeight}
                          $isAnswer={!!cellAnswer}
                          $rows={headerTextAreaRowHeight}
                          dangerouslySetInnerHTML={{
                            __html: getRichtextValue(
                              cellAnswer || header.value,
                              extensions
                            ),
                          }}
                        />
                      )}
                    </Styled.TableCell>
                  );
                })}
              </Styled.TableRow>
            </thead>
          )}
          <tbody>
            {tableBodyRows.map((row, tableBodyIndex) => {
              const rowIndex = table.showRowHeaders
                ? tableBodyIndex + 1
                : tableBodyIndex;
              const editorRows = getEditableCellsRowHeight(row.cells);
              return (
                <Styled.TableRow key={`row-${rowIndex}`}>
                  {row.cells.map((cell, cellIndex) => {
                    const cellAnswer =
                      answers?.find(
                        ans =>
                          ans.answerReferences?.row === rowIndex &&
                          ans.answerReferences.col === cellIndex
                      ) || null;

                    return (
                      <Styled.TableCell
                        key={`row-${rowIndex}-cell-${cellIndex}-header`}
                        $highlight={table.showColumnHeaders && cellIndex === 0}
                      >
                        <>
                          <Styled.TableCellView
                            $highlight={
                              table.showColumnHeaders && cellIndex === 0
                            }
                            $baseFontSize={theme.font.medium.size}
                            $baseLineHeight={theme.font.small.lineHeight}
                            $rows={editorRows}
                            dangerouslySetInnerHTML={{
                              __html: !isCellEmpty(cell.value)
                                ? getRichtextValue(cell.value, extensions)
                                : getRichtextValue(
                                    getRichtextAnswer(cellAnswer),
                                    extensions
                                  ),
                            }}
                          />
                        </>
                      </Styled.TableCell>
                    );
                  })}
                </Styled.TableRow>
              );
            })}
          </tbody>
        </Styled.Table>
      </div>
    </SubActivityPrint>
  );
};

export default TablePrintAnswers;
